import { useEffect, useState } from "react";
import SEO from "./components/SEO";
import AdminPage from "./components/AdminPage";
import CheckoutModal from "./components/CheckoutModal";
import LegalModal from "./components/LegalModal";
import { getAboutDocument, getLegalDocuments, LegalDocument } from "./data/legal";
import {
  IconArrowRight,
  IconCheck,
  IconWhatsapp,
  IconEmail,
  IconPhone,
  IconShield,
  IconChevronDown,
  IconUsers,
  IconAward,
  IconCalendar,
  IconMapPin,
  IconClock,
  IconGlobe,
  BadgeVisa,
  BadgeMastercard,
} from "./components/Icons";
import { Product } from "./data/product";
import { catalog, getCourseById, CatalogCourse } from "./data/catalog";
import { useLanguage } from "./context/LanguageContext";
import { uiText } from "./i18n/ui";

function getCurrencyFormatter(lang: "en" | "tr", currency: string) {
  return new Intl.NumberFormat(lang === "tr" ? "tr-TR" : "en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  });
}

function getContactInfo(lang: "en" | "tr") {
  const t = uiText[lang].contact.labels;
  return [
    {
      icon: IconEmail,
      title: t.email,
      value: "info@haptone.com",
      href: "mailto:info@haptone.com",
    },
    {
      icon: IconPhone,
      title: t.phone,
      value: "+971 58 543 4720",
      href: "tel:+971585434720",
    },
    {
      icon: IconWhatsapp,
      title: t.whatsapp,
      value: "+90 543 171 29 23",
      href: "https://wa.me/905431712923",
    },
    {
      icon: IconMapPin,
      title: t.address1,
      value:
        lang === "tr"
          ? "Dubai Digital Park, Dubai Silicon Oasis, A1 Blok - Dubai - Birleşik Arap Emirlikleri"
          : "Dubai Digital Park, Dubai Silicon Oasis, Building A1 - Dubai - United Arab Emirates",
      href: undefined,
    },
    {
      icon: IconMapPin,
      title: t.address2,
      value:
        lang === "tr"
          ? "Başıbüyük Mah. Süreyyapaşa Başıbüyük Yolu Sk. Teknopark No: 4/1 İç Kapı No: 1, Maltepe / İstanbul, Türkiye"
          : "Başıbüyük Mah. Süreyyapaşa Başıbüyük Yolu Sk. Teknopark No: 4/1 İç Kapı No: 1, Maltepe / Istanbul, Turkiye",
      href: undefined,
    },
  ];
}

function useCheckout() {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  return { isOpen, open: () => setIsOpen(true), close: () => setIsOpen(false) };
}

type PaymentResultStatus = "success" | "failed" | "error";

function usePaymentResult() {
  const [status, setStatus] = useState<PaymentResultStatus | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tamiStatus = params.get("tami_status");
    if (tamiStatus === "success" || tamiStatus === "failed" || tamiStatus === "error") {
      setStatus(tamiStatus);
      params.delete("tami_status");
      params.delete("tami_order");
      const query = params.toString();
      window.history.replaceState({}, "", `${window.location.pathname}${query ? `?${query}` : ""}`);
    }
  }, []);

  return { status, dismiss: () => setStatus(null) };
}

interface PaymentResultText {
  successTitle: string;
  successBody: string;
  failedTitle: string;
  failedBody: string;
  errorTitle: string;
  errorBody: string;
  dismiss: string;
}

function PaymentResultBanner({
  status,
  onDismiss,
  t,
}: {
  status: PaymentResultStatus;
  onDismiss: () => void;
  t: PaymentResultText;
}) {
  const isSuccess = status === "success";
  const title = isSuccess ? t.successTitle : status === "failed" ? t.failedTitle : t.errorTitle;
  const body = isSuccess ? t.successBody : status === "failed" ? t.failedBody : t.errorBody;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-primary/80 backdrop-blur-sm" onClick={onDismiss} aria-hidden="true" />
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 sm:p-8 text-center">
        <div
          className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-4 ${
            isSuccess ? "bg-accent/10 text-accent" : "bg-red-50 text-red-600"
          }`}
        >
          {isSuccess ? <IconCheck size={22} /> : <IconArrowRight size={22} className="rotate-90" />}
        </div>
        <h3 className="text-lg font-bold text-primary mb-2">{title}</h3>
        <p className="text-muted text-sm leading-relaxed mb-6">{body}</p>
        <button
          onClick={onDismiss}
          className="w-full inline-flex items-center justify-center gap-2 bg-accent hover:bg-accent-light text-white font-semibold px-6 py-3 rounded-xl transition-all duration-300"
        >
          {t.dismiss}
        </button>
      </div>
    </div>
  );
}

function Faq({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-border rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left bg-white hover:bg-light transition-colors"
      >
        <span className="font-semibold text-primary">{question}</span>
        <IconChevronDown
          size={20}
          className={`flex-shrink-0 text-muted transition-transform duration-300 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {open && (
        <div className="px-5 pb-4 text-muted leading-relaxed">{answer}</div>
      )}
    </div>
  );
}

function ProgramItinerary({ product }: { product: Product }) {
  const [activeDay, setActiveDay] = useState(0);
  const day = product.programDays[activeDay];

  return (
    <div>
      <div className="flex flex-wrap gap-2 bg-white rounded-2xl border border-border p-2">
        {product.programDays.map((d, i) => (
          <button
            key={d.dayLabel}
            onClick={() => setActiveDay(i)}
            className={`flex-1 min-w-[90px] rounded-xl px-3 py-2.5 text-center transition-colors ${
              activeDay === i
                ? "bg-accent text-white"
                : "text-primary hover:bg-light"
            }`}
          >
            <div className="text-sm font-semibold">{d.dayLabel}</div>
            <div className={`text-xs ${activeDay === i ? "text-white/80" : "text-muted"}`}>
              {d.date}
            </div>
          </button>
        ))}
      </div>

      <div className="mt-4 rounded-2xl border border-border bg-white p-5 sm:p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <span className="text-xs font-semibold uppercase tracking-widest text-accent">
              {day.dayLabel} · {day.date} · {day.weekday}
            </span>
            <h3 className="mt-1 text-xl font-bold text-primary">{day.title}</h3>
          </div>
          <span className="inline-flex items-center gap-1.5 bg-light text-muted text-xs font-medium px-3 py-1.5 rounded-full">
            <IconClock size={14} />
            {day.timeRange}
          </span>
        </div>

        <div className="mt-5 divide-y divide-border">
          {day.sessions.map((session) => (
            <div key={session.time} className="flex gap-4 py-3">
              <span className="flex-shrink-0 w-28 text-sm font-semibold text-accent">
                {session.time}
              </span>
              <span className="text-sm text-primary">{session.title}</span>
            </div>
          ))}
        </div>

        <div className="mt-5 rounded-xl bg-dark text-white px-4 py-3 flex items-start gap-2.5">
          <IconMapPin size={16} className="flex-shrink-0 mt-0.5 text-accent-light" />
          <span className="text-sm text-white/80">{day.dubaiExperience}</span>
        </div>
      </div>
    </div>
  );
}


function CourseDetailPage({ course, onBack }: { course: CatalogCourse; onBack: () => void }) {
  const { lang, toggleLang } = useLanguage();
  const t = uiText[lang];
  const product = course.getProduct(lang);
  const contactInfo = getContactInfo(lang);
  const currencyFormatter = getCurrencyFormatter(course.paymentType === "external" ? "en" : lang, product.currency);
  const checkout = useCheckout();
  const paymentResult = usePaymentResult();
  const [activeImage, setActiveImage] = useState(0);
  const [legalDoc, setLegalDoc] = useState<LegalDocument | null>(null);
  const isExternal = course.paymentType === "external";

  useEffect(() => {
    setActiveImage(0);
  }, [course.id]);

  useEffect(() => {
    document.body.style.overflow = legalDoc ? "hidden" : checkout.isOpen ? "hidden" : "";
  }, [legalDoc, checkout.isOpen]);

  const discountPercent = product.originalPrice
    ? Math.round(100 - (product.price / product.originalPrice) * 100)
    : null;

  const whatsappHref = `https://wa.me/905431712923?text=${encodeURIComponent(t.contact.whatsappMessage)}`;

  const handleBuyNow = () => {
    if (isExternal && course.externalPaymentUrl) {
      window.open(course.externalPaymentUrl, "_blank", "noopener,noreferrer");
      return;
    }
    checkout.open();
  };

  return (
    <>
      <SEO
        title={`${product.name} | Haptone Shop`}
        description={product.summary}
      />

      {/* ANNOUNCEMENT BAR */}
      <div className="bg-primary text-white text-center text-xs sm:text-sm font-medium py-2 px-4">
        {product.seatsNote}
      </div>

      {/* HEADER */}
      <header className="sticky top-0 z-40 bg-white border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4 sm:gap-6">
            <button type="button" onClick={onBack} className="flex-shrink-0" aria-label="Haptone">
              <img src="/logo.png" alt="Haptone" className="h-8 w-auto" width={100} height={32} />
            </button>
          </div>
          <div className="flex items-center gap-4 sm:gap-6">
            <a href="#contact" className="hidden sm:inline text-sm font-semibold text-primary hover:text-accent transition-colors">
              {t.nav.contact}
            </a>
            <button
              type="button"
              onClick={toggleLang}
              aria-label="Change language"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-accent transition-colors"
            >
              <IconGlobe size={16} />
              <span className={lang === "en" ? "text-accent" : ""}>EN</span>
              <span className="text-border">/</span>
              <span className={lang === "tr" ? "text-accent" : ""}>TR</span>
            </button>
          </div>
        </div>
      </header>

      <main id="top" className="pb-24 lg:pb-0">
        {/* EVENT META BAR */}
        <div className="bg-primary">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 sm:grid-cols-4 divide-x divide-white/10">
            {[
              { icon: IconCalendar, label: t.meta.date, value: product.eventMeta.dateRange },
              { icon: IconMapPin, label: t.meta.location, value: product.eventMeta.location },
              { icon: IconClock, label: t.meta.training, value: product.eventMeta.duration },
              { icon: IconGlobe, label: t.meta.language, value: product.eventMeta.language },
            ].map(({ icon: Icon, label, value }) => (
              <div key={label} className="flex items-start gap-2.5 py-4 px-3 sm:px-5">
                <Icon size={18} className="flex-shrink-0 text-accent-light mt-0.5" />
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-white/50">
                    {label}
                  </div>
                  <div className="text-white text-sm font-medium leading-snug">{value}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* PRODUCT / BUY BOX */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14">
            {/* GALLERY */}
            <div>
              <div className="rounded-2xl overflow-hidden border border-border bg-light aspect-[4/5]">
                <img
                  src={product.galleryImages[activeImage]}
                  alt={product.shortName}
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="mt-3 grid grid-cols-3 gap-3">
                {product.galleryImages.map((img, i) => (
                  <button
                    key={img}
                    onClick={() => setActiveImage(i)}
                    className={`rounded-xl overflow-hidden border-2 aspect-[4/5] bg-light transition-colors ${
                      activeImage === i ? "border-accent" : "border-border"
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-contain" />
                  </button>
                ))}
              </div>
            </div>

            {/* BUY PANEL */}
            <div>
              <div className="flex flex-wrap gap-2">
                {product.topics.map((topic) => (
                  <span
                    key={topic}
                    className="inline-flex items-center gap-1.5 text-accent bg-accent/10 text-xs font-semibold uppercase tracking-widest px-3 py-1.5 rounded-full"
                  >
                    {topic}
                  </span>
                ))}
              </div>

              <h1 className="mt-4 text-2xl sm:text-3xl lg:text-[2rem] font-bold text-primary leading-tight">
                {product.name}
              </h1>

              <p className="mt-5 text-muted leading-relaxed">{product.summary}</p>

              <div className="mt-6 flex items-end gap-3 flex-wrap">
                <span className="text-3xl sm:text-4xl font-bold text-primary">
                  {currencyFormatter.format(product.price)}
                </span>
                {product.originalPrice && (
                  <span className="text-lg text-muted line-through mb-1">
                    {currencyFormatter.format(product.originalPrice)}
                  </span>
                )}
                {discountPercent && (
                  <span className="mb-1 text-sm font-semibold text-white bg-accent px-2.5 py-1 rounded-lg">
                    {discountPercent}% {t.buyBox.discountOff}
                  </span>
                )}
              </div>
              <p className="mt-1 text-xs text-muted">{product.priceNote}</p>
              {!isExternal && (
                <p className="mt-1 text-xs font-medium text-accent">
                  {t.buyBox.depositNote(currencyFormatter.format(product.depositAmount))}
                </p>
              )}

              <div className="mt-6 space-y-3">
                {product.highlights.map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-5 h-5 bg-accent/10 rounded-full flex items-center justify-center mt-0.5">
                      <IconCheck size={12} className="text-accent" />
                    </div>
                    <span className="text-primary text-sm leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleBuyNow}
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-accent hover:bg-accent-light text-white font-semibold px-8 py-4 rounded-xl transition-all duration-300 hover:shadow-xl hover:shadow-accent/30"
                >
                  {t.buyBox.registerBuyNow}
                  <IconArrowRight size={18} />
                </button>
                <a
                  href="https://wa.me/905431712923"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 border-2 border-border text-primary font-semibold px-6 py-4 rounded-xl hover:bg-light transition-all duration-300"
                >
                  <IconWhatsapp size={18} />
                  {t.buyBox.askQuestion}
                </a>
              </div>

              <div className="mt-6 grid grid-cols-3 gap-3 text-center">
                {product.badges.slice(0, 3).map((badge, i) => (
                  <div
                    key={badge}
                    className="rounded-xl border border-border bg-light px-2 py-3 flex flex-col items-center gap-1.5"
                  >
                    {i === 0 ? (
                      <IconAward size={18} className="text-accent" />
                    ) : i === 1 ? (
                      <IconUsers size={18} className="text-accent" />
                    ) : (
                      <IconShield size={18} className="text-accent" />
                    )}
                    <span className="text-[11px] leading-tight text-muted font-medium">
                      {badge}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ABOUT THE PROGRAM */}
        <section className="bg-light py-14 lg:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <span className="text-accent font-semibold text-xs uppercase tracking-widest">
                {t.about.eyebrow}
              </span>
              <h2 className="mt-3 text-2xl sm:text-3xl font-bold text-primary leading-tight">
                {product.aboutTitle}
              </h2>
              <div className="mt-5 space-y-4">
                {product.aboutParagraphs.map((p) => (
                  <p key={p} className="text-muted leading-relaxed">
                    {p}
                  </p>
                ))}
              </div>

              <div className="mt-6 flex items-center gap-3 rounded-xl border border-border bg-white p-4">
                <IconMapPin size={18} className="flex-shrink-0 text-accent" />
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted font-semibold">
                    {t.about.trainingCenter}
                  </div>
                  <div className="text-primary font-semibold text-sm">{product.trainingCenter}</div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden border border-border bg-white aspect-[4/5]">
              <img
                src={product.aboutImage}
                alt={product.shortName}
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        </section>

        {/* DAY-BY-DAY ITINERARY */}
        <section className="py-14 lg:py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <span className="text-accent font-semibold text-xs uppercase tracking-widest">
              {t.itinerary.eyebrow}
            </span>
            <h2 className="mt-3 text-2xl sm:text-3xl font-bold text-primary leading-tight">
              {t.itinerary.title}
            </h2>
            <p className="mt-2 text-muted">
              {t.itinerary.subtitle}
            </p>

            <div className="mt-8">
              <ProgramItinerary product={product} />
            </div>
          </div>
        </section>

        {/* WHO IS IT FOR */}
        <section className="py-14 lg:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-primary mb-6">
                {t.audience.title}
              </h2>
              <div className="bg-white rounded-xl border border-border p-5 space-y-4">
                {product.audience.map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-5 h-5 bg-accent/10 rounded-full flex items-center justify-center mt-0.5">
                      <IconCheck size={12} className="text-accent" />
                    </div>
                    <span className="text-primary text-sm leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-primary mb-6">
                {t.payment.title}
              </h2>
              <div className="rounded-xl border border-border bg-white p-5">
                <div className="flex items-center gap-2 text-primary font-semibold text-sm mb-1">
                  <IconShield size={16} className="text-accent" />
                  {t.payment.assuranceTitle}
                </div>
                <p className="text-muted text-sm leading-relaxed">
                  {t.payment.assuranceText}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-14 lg:py-20">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-xl sm:text-2xl font-bold text-primary mb-8">
              {t.faq.title}
            </h2>
            <div className="space-y-3">
              {product.faq.map((item) => (
                <Faq key={item.question} question={item.question} answer={item.answer} />
              ))}
            </div>
          </div>
        </section>

        {/* CONTACT */}
        <section id="contact" className="bg-light py-14 lg:py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <span className="text-accent font-semibold text-xs uppercase tracking-widest">
              {t.contact.eyebrow}
            </span>
            <h2 className="mt-3 text-2xl sm:text-3xl font-bold text-primary leading-tight">
              {t.contact.title}
            </h2>
            <p className="mt-2 text-muted">
              {t.contact.subtitle}
            </p>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {contactInfo.map((item) => (
                <div key={item.title} className="flex items-start gap-4 bg-white rounded-xl border border-border p-4">
                  <div className="flex-shrink-0 w-11 h-11 bg-accent/10 rounded-xl flex items-center justify-center">
                    <item.icon size={18} className="text-accent" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-widest text-muted">
                      {item.title}
                    </div>
                    {item.href ? (
                      <a
                        href={item.href}
                        target={item.href.startsWith("http") ? "_blank" : undefined}
                        rel={item.href.startsWith("http") ? "noopener noreferrer" : undefined}
                        className="text-primary font-medium hover:text-accent transition-colors"
                      >
                        {item.value}
                      </a>
                    ) : (
                      <p className="text-primary font-medium">{item.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <a
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20BD5A] text-white font-semibold px-6 py-3 rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-[#25D366]/30"
            >
              <IconWhatsapp size={18} />
              {t.contact.whatsappCta}
            </a>
          </div>
        </section>

        {/* FINAL CALL TO ACTION */}
        <section className="bg-primary py-14 lg:py-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-3">
              {t.cta.title}
            </h2>
            <p className="text-white/60 mb-6">{product.priceNote}</p>
            <button
              onClick={handleBuyNow}
              className="inline-flex items-center justify-center gap-2 bg-accent hover:bg-accent-light text-white font-semibold px-10 py-4 rounded-xl transition-all duration-300 hover:shadow-xl hover:shadow-accent/30"
            >
              {currencyFormatter.format(product.price)} — {t.cta.registerNow}
              <IconArrowRight size={18} />
            </button>
          </div>
        </section>
      </main>

      <footer className="bg-dark text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            <a href="https://haptone.com" target="_blank" rel="noopener noreferrer" aria-label="Haptone">
              <img src="/logo.png" alt="Haptone" className="h-8 w-auto brightness-0 invert" width={100} height={32} />
            </a>

            <div className="flex items-center gap-4">
              <a
                href="https://wa.me/905431712923"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-[#25D366] transition-colors duration-300"
                aria-label="WhatsApp"
              >
                <IconWhatsapp size={18} />
              </a>
              <a
                href="mailto:info@haptone.com"
                className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-secondary transition-colors duration-300"
                aria-label="Email"
              >
                <IconEmail size={18} />
              </a>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-white/10 flex flex-wrap items-center justify-center sm:justify-start gap-x-6 gap-y-2 text-white/60 text-sm">
            <a href="#contact" className="hover:text-white transition-colors">{t.footer.contact}</a>
            <span>info@haptone.com</span>
            <span>+971 58 543 4720</span>
          </div>

          <div className="mt-8 pt-6 border-t border-white/10 flex flex-wrap items-center justify-center sm:justify-start gap-x-6 gap-y-2">
            {[getAboutDocument(lang), ...getLegalDocuments(lang)].map((doc) => (
              <button
                key={doc.slug}
                type="button"
                onClick={() => setLegalDoc(doc)}
                className="text-white/60 hover:text-white text-sm transition-colors"
              >
                {doc.title}
              </button>
            ))}
          </div>

          <div className="mt-6 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-white/50 text-xs font-medium uppercase tracking-widest">
              <IconShield size={14} />
              {t.footer.securePayments}
            </div>
            <div className="flex items-center gap-2">
              <BadgeVisa />
              <BadgeMastercard />
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-white/10 text-center text-white/40 text-sm">
            © {new Date().getFullYear()} Haptone. {t.footer.rightsReserved}
          </div>
        </div>
      </footer>

      {/* MOBILE STICKY BUY BAR */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-border px-4 py-3 flex items-center justify-between gap-4">
        <div>
          <div className="text-primary font-bold text-lg leading-none">
            {currencyFormatter.format(product.price)}
          </div>
          {product.originalPrice && (
            <div className="text-xs text-muted line-through">
              {currencyFormatter.format(product.originalPrice)}
            </div>
          )}
        </div>
        <button
          onClick={handleBuyNow}
          className="flex-1 max-w-[220px] inline-flex items-center justify-center gap-2 bg-accent hover:bg-accent-light text-white font-semibold px-6 py-3 rounded-xl transition-all duration-300"
        >
          {t.nav.buyNow}
        </button>
      </div>

      {checkout.isOpen && !isExternal && (
        <CheckoutModal product={product} courseId={course.id} onClose={checkout.close} />
      )}
      {legalDoc && <LegalModal doc={legalDoc} onClose={() => setLegalDoc(null)} />}
      {paymentResult.status && (
        <PaymentResultBanner status={paymentResult.status} onDismiss={paymentResult.dismiss} t={t.paymentResult} />
      )}
    </>
  );
}

function LibraryPage({ onSelect }: { onSelect: (id: string) => void }) {
  const { lang, toggleLang } = useLanguage();
  const t = uiText[lang];

  return (
    <>
      <SEO title={`${t.library.title} | Haptone Shop`} description={t.library.subtitle} />

      {/* HEADER */}
      <header className="sticky top-0 z-40 bg-white border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <a href="https://haptone.com" target="_blank" rel="noopener noreferrer" className="flex-shrink-0" aria-label="Haptone">
            <img src="/logo.png" alt="Haptone" className="h-8 w-auto" width={100} height={32} />
          </a>
          <button
            type="button"
            onClick={toggleLang}
            aria-label="Change language"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-accent transition-colors"
          >
            <IconGlobe size={16} />
            <span className={lang === "en" ? "text-accent" : ""}>EN</span>
            <span className="text-border">/</span>
            <span className={lang === "tr" ? "text-accent" : ""}>TR</span>
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <span className="text-accent font-semibold text-xs uppercase tracking-widest">{t.library.eyebrow}</span>
        <h1 className="mt-3 text-3xl sm:text-4xl font-bold text-primary leading-tight">{t.library.title}</h1>
        <p className="mt-3 text-muted max-w-2xl">{t.library.subtitle}</p>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {catalog.map((course) => {
            const product = course.getProduct(lang);
            const currencyFormatter = getCurrencyFormatter(course.paymentType === "external" ? "en" : lang, product.currency);
            return (
              <button
                key={course.id}
                type="button"
                onClick={() => onSelect(course.id)}
                className="text-left bg-white rounded-2xl border border-border overflow-hidden hover:shadow-xl hover:border-accent/40 transition-all duration-300 flex flex-col"
              >
                <div className="aspect-[4/5] bg-light overflow-hidden">
                  <img src={course.thumbnail} alt={product.shortName} className="w-full h-full object-cover" />
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <h2 className="font-bold text-primary leading-snug">{product.shortName}</h2>
                  <p className="mt-2 text-sm text-muted leading-relaxed line-clamp-3 flex-1">{product.summary}</p>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="font-bold text-primary">{currencyFormatter.format(product.price)}</span>
                    <span className="inline-flex items-center gap-1.5 text-accent text-sm font-semibold">
                      {t.library.viewDetails}
                      <IconArrowRight size={14} />
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </main>

      <footer className="bg-dark text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center text-white/50 text-sm">
          © {new Date().getFullYear()} Haptone. {t.footer.rightsReserved}
        </div>
      </footer>
    </>
  );
}

function getCourseIdFromLocation(): string | null {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get("course");
}

function isAdminRoute(): boolean {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).has("admin");
}

export default function App() {
  const [selectedId, setSelectedId] = useState<string | null>(() => {
    const id = getCourseIdFromLocation();
    return id && getCourseById(id) ? id : null;
  });

  useEffect(() => {
    const onPopState = () => {
      const id = getCourseIdFromLocation();
      setSelectedId(id && getCourseById(id) ? id : null);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  // Admin panel — accessible via ?admin query parameter
  if (isAdminRoute()) {
    return <AdminPage />;
  }

  const navigateToCourse = (id: string | null) => {
    setSelectedId(id);
    const url = id ? `${window.location.pathname}?course=${encodeURIComponent(id)}` : window.location.pathname;
    window.history.pushState({}, "", url);
    window.scrollTo(0, 0);
  };

  const course = selectedId ? getCourseById(selectedId) : undefined;

  if (course) {
    return <CourseDetailPage course={course} onBack={() => navigateToCourse(null)} />;
  }

  return <LibraryPage onSelect={(id) => navigateToCourse(id)} />;
}
